import {
  LoginButton,
  LoginTitle,
  LoginContainer,
  LoginPassword,
} from './components'
import { PasswordVisibilityAdornment } from '../../components/inputs/PasswordVisibilityAdornment'
import { NoticeText } from '../../components/labels/NoticeText'
import { intl } from '../../../i18n'
import { TextField, TextFieldProps, styled } from '@mui/material'
import { useCallback, useMemo, useState, KeyboardEvent } from 'react'
import { useHistory, useRouteMatch } from 'react-router'
import { usePasswordField } from '../../hooks/usePasswordField'
import { RESET_CODE_LENGTH, useResetCodeInput } from './hooks/resetCodeInput'
import { AuthError } from '../../../error/AuthError'
import { ErrorMessage } from './components/AuthErrorMessage'
import { useAuthenticateUseCase } from '../../../applications/usecases/authentication/authenticationUseCases'
import { colorPalette } from '../../style/colorPalette'
import { whitespaceNoticeViewOf } from '../../../utils/password'

const LoginText = styled('div')({
  fontSize: '14px',
  fontWeight: '400',
  lineHeight: '21px',
  letterSpacing: '0em',
  textAlign: 'center',
  color: colorPalette.monotone[4],
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  whiteSpace: 'pre-line',
})
const CodeBox = styled('div')({
  display: 'flex',
  gap: '8px',
})

const SingleNumberInput = styled((props: TextFieldProps) => (
  <TextField
    variant="outlined"
    autoComplete="off"
    type="tel"
    inputProps={{ inputMode: 'numeric', maxLength: 1, pattern: '[0-9]' }}
    {...props}
  />
))({
  width: '42px',
  '& .MuiOutlinedInput-input': {
    height: '24px',
    padding: '20px 16px',
    color: colorPalette.monotone[9],
    caretColor: colorPalette.skyBlue[7],
    fontSize: '16px !important',
    fontWeight: 600,
  },
  '& .MuiOutlinedInput-root': {
    height: '54px',
    '&:focus-within': {
      backgroundColor: colorPalette.blue[0],
    },
    '& fieldset': {
      borderColor: colorPalette.monotone[2],
    },
    '&:hover fieldset': {
      borderColor: colorPalette.skyBlue[7],
    },
    '&.Mui-focused fieldset': {
      borderColor: colorPalette.skyBlue[7],
      borderWidth: '1px',
    },
  },
})

export const ForgotPasswordSubmit = () => {
  const match = useRouteMatch<{ email: string }>()
  const email = useMemo(() => {
    return match.params.email
  }, [match])
  const {
    resetCode,
    refs,
    displayValueOf,
    onChange: onChangeResetCode,
    onKeyDown: onKeyDownResetCode,
    onFocus: onFocusResetCode,
    onPaste: onPasteResetCode,
    onCompositionEnd: onCompositionEndResetCode,
  } = useResetCodeInput()

  const newPassword = usePasswordField()
  const newPasswordConfirm = usePasswordField()

  const [authError, setAuthError] = useState<AuthError | undefined>(undefined)
  const { resetPasswordSubmit } = useAuthenticateUseCase()
  const history = useHistory()
  const onSubmit = useCallback(async () => {
    if (resetCode.length !== RESET_CODE_LENGTH) {
      setAuthError(new AuthError('NO_RESET_CODE'))
      return
    }
    if (!newPassword.value || !newPasswordConfirm.value) {
      setAuthError(new AuthError('NO_PASSWORD_INPUT'))
      return
    }
    if (newPassword.value !== newPasswordConfirm.value) {
      setAuthError(new AuthError('PASSWORD_CONFIRM_UNMATCH'))
      return
    }
    resetPasswordSubmit(email, resetCode, newPassword.value)
      .then(() => history.push('/changedPassword'))
      .catch(err => {
        if (!(err instanceof AuthError)) {
          throw err
        }
        setAuthError(err)
      })
  }, [
    email,
    history,
    newPassword.value,
    newPasswordConfirm.value,
    resetCode,
    resetPasswordSubmit,
  ])
  const onEnter = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (event.key === 'Enter') {
        onSubmit()
      }
    },
    [onSubmit]
  )
  const back = useCallback(() => {
    history.push('/forgotPassword')
  }, [history])
  return (
    <>
      <LoginContainer>
        <LoginTitle>
          {intl.formatMessage({ id: 'forgotPassword.resetPassword' })}
        </LoginTitle>
        <LoginText>
          {intl.formatMessage({
            id: 'forgotPasswordSubmit.ifNotReceiveAuthCode',
          })}
        </LoginText>
        {authError && <ErrorMessage authError={authError} />}
        <LoginText>
          {intl.formatMessage({ id: 'forgotPasswordSubmit.authCode' })}
        </LoginText>
        <CodeBox>
          {refs.map((ref, index) => (
            <SingleNumberInput
              key={index}
              inputRef={ref}
              value={displayValueOf(index)}
              onChange={e => onChangeResetCode(e, index)}
              onKeyDown={e => onKeyDownResetCode(e, index)}
              onFocus={onFocusResetCode}
              onPaste={e => onPasteResetCode(e, index)}
              onCompositionEnd={() => onCompositionEndResetCode(index)}
              autoComplete={index === 0 ? 'one-time-code' : 'off'}
            />
          ))}
        </CodeBox>
        <LoginPassword
          label={intl.formatMessage({ id: 'changePassword.newPassword' })}
          type={newPassword.inputType}
          autoComplete="new-password"
          value={newPassword.value}
          onChange={newPassword.onChange}
          onKeyDown={e => {
            newPassword.onKeyDown(e)
            onEnter(e)
          }}
          InputProps={{
            endAdornment: (
              <PasswordVisibilityAdornment
                visible={newPassword.showPassword}
                onToggle={newPassword.toggleShowPassword}
              />
            ),
          }}
          helperText={
            <NoticeText
              {...whitespaceNoticeViewOf(newPassword.whitespaceNotice)}
            />
          }
        />
        <LoginPassword
          label={intl.formatMessage({
            id: 'changePassword.newPasswordForConfirm',
          })}
          type={newPasswordConfirm.inputType}
          autoComplete="off"
          value={newPasswordConfirm.value}
          onChange={newPasswordConfirm.onChange}
          onKeyDown={e => {
            newPasswordConfirm.onKeyDown(e)
            onEnter(e)
          }}
          InputProps={{
            endAdornment: (
              <PasswordVisibilityAdornment
                visible={newPasswordConfirm.showPassword}
                onToggle={newPasswordConfirm.toggleShowPassword}
              />
            ),
          }}
          helperText={
            <NoticeText
              {...whitespaceNoticeViewOf(newPasswordConfirm.whitespaceNotice)}
            />
          }
        />
        <LoginButton color={'skyBlue'} onClick={onSubmit}>
          {intl.formatMessage({ id: 'forgotPasswordSubmit.setPassword' })}
        </LoginButton>
        <LoginButton color={'monotone'} onClick={back}>
          {intl.formatMessage({ id: 'back' })}
        </LoginButton>
      </LoginContainer>
    </>
  )
}
